import { $api } from '~/utils/api'
import { logApiError } from '~/utils/logger'
import { buildQueryString } from '~/utils/queryParams'

interface FaqFilters {
  page?: number
  limit?: number
  search?: string
  category?: string
  isActive?: boolean
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

interface FaqInput {
  question: string
  answer: string
  category?: string | null
  display_order?: number
  is_active?: boolean
}

export const useFaqs = () => {
  const getFaqs = async (filters: FaqFilters = {}) => {
    try {
      const queryString = buildQueryString(filters)

      return await $api(`/api/faqs${queryString ? `?${queryString}` : ''}`)
    }
    catch (error) {
      logApiError('/api/faqs', error, { filters })
      throw error
    }
  }

  const getFaq = async (id: string) => {
    try {
      return await $api(`/api/faqs/${id}`)
    }
    catch (error) {
      logApiError(`/api/faqs/${id}`, error)
      throw error
    }
  }

  const createFaq = async (data: FaqInput) => {
    try {
      return await $api('/api/faqs', {
        method: 'POST',
        body: data,
      })
    }
    catch (error) {
      logApiError('/api/faqs', error, { data })
      throw error
    }
  }

  const updateFaq = async (id: string, data: Partial<FaqInput>) => {
    try {
      return await $api(`/api/faqs/${id}`, {
        method: 'PATCH',
        body: data,
      })
    }
    catch (error) {
      logApiError(`/api/faqs/${id}`, error, { data })
      throw error
    }
  }

  const deleteFaq = async (id: string) => {
    try {
      return await $api(`/api/faqs/${id}`, {
        method: 'DELETE',
      })
    }
    catch (error) {
      logApiError(`/api/faqs/${id}`, error)
      throw error
    }
  }

  const updateFaqOrder = async (items: Array<{ id: string, display_order: number }>) => {
    try {
      // Update order for multiple FAQs
      const promises = items.map(item =>
        updateFaq(item.id, { display_order: item.display_order }),
      )

      return await Promise.all(promises)
    }
    catch (error) {
      logApiError('/api/faqs (bulk update)', error, { items })
      throw error
    }
  }

  // Category CRUD operations
  const getFaqCategories = async (includeInactive = false) => {
    try {
      const queryString = includeInactive ? '?includeInactive=true' : ''

      return await $api(`/api/faq-categories${queryString}`)
    }
    catch (error) {
      logApiError('/api/faq-categories', error)
      throw error
    }
  }

  const createFaqCategory = async (data: { name: string, description?: string, display_order?: number }) => {
    try {
      return await $api('/api/faq-categories', {
        method: 'POST',
        body: data,
      })
    }
    catch (error) {
      logApiError('/api/faq-categories', error, { action: 'create' })
      throw error
    }
  }

  const updateFaqCategory = async (id: string, data: { name?: string, description?: string, display_order?: number, is_active?: boolean }) => {
    try {
      return await $api(`/api/faq-categories/${id}`, {
        method: 'PATCH',
        body: data,
      })
    }
    catch (error) {
      logApiError(`/api/faq-categories/${id}`, error, { action: 'update' })
      throw error
    }
  }

  const deleteFaqCategory = async (id: string) => {
    try {
      return await $api(`/api/faq-categories/${id}`, {
        method: 'DELETE',
      })
    }
    catch (error) {
      logApiError(`/api/faq-categories/${id}`, error, { action: 'delete' })
      throw error
    }
  }

  return {
    getFaqs,
    getFaq,
    createFaq,
    updateFaq,
    deleteFaq,
    updateFaqOrder,
    getFaqCategories,
    createFaqCategory,
    updateFaqCategory,
    deleteFaqCategory,
  }
}
